import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMessageCircle, FiX, FiSend, FiUser, FiPhone, FiVideo, FiMic, FiMicOff } = FiIcons;

function LiveChatSupport() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'agent',
      text: "Hi! I'm Sarah from Halifax customer service. How can I help you today?",
      timestamp: new Date(),
      agent: { name: 'Sarah MacLeod', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=40&h=40&fit=crop&crop=face' }
    }
  ]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [isVideoCall, setIsVideoCall] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const messagesEndRef = useRef(null);

  const quickActions = [
    'Schedule a test drive',
    'Get financing information',
    'Ask about trade-in value',
    'Service appointment',
    'Warranty information'
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = () => {
    if (!currentMessage.trim()) return;

    const newMessage = {
      id: Date.now(),
      type: 'customer',
      text: currentMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);
    setCurrentMessage('');
    setIsAgentTyping(true);

    // Simulate agent response
    setTimeout(() => {
      const agentResponse = {
        id: Date.now() + 1,
        type: 'agent',
        text: "I'd be happy to help you with that! Let me pull up the information for you. In Halifax, we have several options available that would work perfectly for your needs.",
        timestamp: new Date(),
        agent: { name: 'Sarah MacLeod', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=40&h=40&fit=crop&crop=face' }
      };
      setMessages(prev => [...prev, agentResponse]);
      setIsAgentTyping(false);
    }, 2000);
  };

  const sendQuickAction = (action) => {
    const newMessage = {
      id: Date.now(),
      type: 'customer',
      text: action,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);
    setIsAgentTyping(true);

    setTimeout(() => {
      let responseText = "Great choice! Let me help you with that.";
      
      if (action.includes('test drive')) {
        responseText = "Perfect! I can schedule your Halifax test drive right now. What vehicle are you interested in and when would work best for you?";
      } else if (action.includes('financing')) {
        responseText = "I'd be happy to discuss our Halifax financing options. We have competitive Canadian rates starting at 3.9% APR. What's your budget range?";
      } else if (action.includes('trade-in')) {
        responseText = "Excellent! I can get you an instant trade-in estimate. What year, make, and model is your current vehicle? How many kilometres?";
      }

      const agentResponse = {
        id: Date.now() + 1,
        type: 'agent',
        text: responseText,
        timestamp: new Date(),
        agent: { name: 'Sarah MacLeod', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=40&h=40&fit=crop&crop=face' }
      };
      setMessages(prev => [...prev, agentResponse]);
      setIsAgentTyping(false);
    }, 1500);
  };

  const startVideoCall = () => {
    setIsVideoCall(true);
    const videoMessage = {
      id: Date.now(),
      type: 'system',
      text: "Video call started with Sarah MacLeod - Halifax Customer Service",
      timestamp: new Date()
    };
    setMessages(prev => [...prev, videoMessage]);
  };

  return (
    <>
      {/* Chat Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-24 right-6 w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full shadow-lg flex items-center justify-center z-40 ${
          isOpen ? 'hidden' : 'block'
        }`}
      >
        <SafeIcon icon={FiMessageCircle} className="w-6 h-6 text-white" />
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
          <span className="text-xs text-white font-bold">1</span>
        </div>
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-xl shadow-2xl z-50 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <img
                  src="https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=40&h=40&fit=crop&crop=face"
                  alt="Sarah MacLeod"
                  className="w-10 h-10 rounded-full border-2 border-white"
                />
                <div>
                  <h3 className="text-white font-semibold">Sarah MacLeod</h3>
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-green-300 rounded-full"></div>
                    <p className="text-green-100 text-xs">Online - Halifax Support</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={startVideoCall}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <SafeIcon icon={FiVideo} className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Video Call Overlay */}
            {isVideoCall && (
              <div className="absolute inset-0 bg-gray-900 z-10 flex flex-col">
                <div className="flex-1 relative bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                  <img
                    src="https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=200&h=200&fit=crop&crop=face"
                    alt="Sarah MacLeod"
                    className="w-32 h-32 rounded-full border-4 border-green-500"
                  />
                  <div className="absolute top-4 left-4 bg-black/50 rounded-lg px-3 py-1">
                    <p className="text-white text-sm">Sarah MacLeod - Halifax</p>
                  </div>
                  <div className="absolute top-4 right-4 w-24 h-32 bg-gray-700 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiUser} className="w-8 h-8 text-gray-400" />
                  </div>
                </div>
                <div className="bg-gray-800 p-4 flex justify-center space-x-4">
                  <button
                    onClick={() => setIsMuted(!isMuted)}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                      isMuted ? 'bg-red-500 text-white' : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                    }`}
                  >
                    <SafeIcon icon={isMuted ? FiMicOff : FiMic} className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setIsVideoCall(false)}
                    className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg) => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.type === 'customer' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] ${
                    msg.type === 'customer' 
                      ? 'bg-blue-600 text-white' 
                      : msg.type === 'system'
                      ? 'bg-gray-100 text-gray-600 text-center'
                      : 'bg-gray-100 text-gray-900'
                  } p-3 rounded-lg`}>
                    {msg.agent && (
                      <div className="flex items-center space-x-2 mb-1">
                        <img
                          src={msg.agent.avatar}
                          alt={msg.agent.name}
                          className="w-5 h-5 rounded-full"
                        />
                        <span className="text-xs font-medium">{msg.agent.name}</span>
                      </div>
                    )}
                    <p className="text-sm">{msg.text}</p>
                    <p className={`text-xs mt-1 ${
                      msg.type === 'customer' ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      {msg.timestamp.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                    </p>
                  </div>
                </motion.div>
              ))}

              {isAgentTyping && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start"
                >
                  <div className="bg-gray-100 p-3 rounded-lg flex items-center space-x-2">
                    <img
                      src="https://images.unsplash.com/photo-1494790108755-2616b612b47c?w=20&h=20&fit=crop&crop=face"
                      alt="Sarah"
                      className="w-5 h-5 rounded-full"
                    />
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Actions */}
            <div className="p-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">Quick actions:</p>
              <div className="flex flex-wrap gap-1 mb-3">
                {quickActions.slice(0, 3).map((action, index) => (
                  <button
                    key={index}
                    onClick={() => sendQuickAction(action)}
                    disabled={isAgentTyping}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    {action}
                  </button>
                ))}
              </div>
            </div>

            {/* Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Type your message..."
                  disabled={isAgentTyping}
                  className="flex-1 p-2 border border-gray-200 rounded-lg focus:border-green-500 focus:outline-none text-sm disabled:opacity-50"
                />
                <button
                  onClick={sendMessage}
                  disabled={isAgentTyping || !currentMessage.trim()}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <SafeIcon icon={FiSend} className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Halifax live support • Average response: 30 seconds
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default LiveChatSupport;